import { api, startLoading, endLoading } from '@/api'
import { downloadReport } from './complex/downloadReport'
import * as types from './types'

export default {
  /**
   * Reset survey value
   */
  resetSurvey ({ commit }) {
    commit(types.RESET_SURVEY)
  },
  /**
  * Get all surveys
  * @param {Object} params - SearchOptions object
  */
  async getSurveysByLocation ({ commit, dispatch }, params) {
    let loaderName = 'getSurveysByLocation'
    startLoading(dispatch, loaderName)

    return new Promise((resolve, reject) => {
      api
        .getSurveysByLocation(params)
        .then((response) => {
          commit(types.SET_SURVEYS, response.data.items)
          endLoading(dispatch, loaderName)
          resolve(response.data.count)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Get surveys assigned to user
  * @param {Object} params - SearchOptions object
  */
  async getSurveysAssignedToUser ({ commit, dispatch }, params) {
    let loaderName = 'getSurveysAssignedToUser'
    startLoading(dispatch, loaderName)

    return new Promise((resolve, reject) => {
      api
        .getSurveysAssignedToUser(params)
        .then((response) => {
          commit(types.SET_SURVEYS, response.data.items)
          endLoading(dispatch, loaderName)
          resolve(response.data.count)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Get all survey types
  */
  async getSurveyTypes ({ commit, dispatch }) {
    let loaderName = 'getSurveyTypes'
    startLoading(dispatch, loaderName)
    return new Promise((resolve, reject) => {
      api
        .getSurveyTypes()
        .then((response) => {
          commit(types.SET_SURVEY_TYPES, response.data)
          endLoading(dispatch, loaderName)
          resolve(response.data)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Get survey by ID
  * @param {Number} surveyID - Survey ID
  */
  async getSurvey ({ commit, dispatch }, surveyID) {
    let loaderName = 'getSurvey'
    startLoading(dispatch, loaderName)
    return new Promise((resolve, reject) => {
      api
        .getSurvey(surveyID)
        .then((response) => {
          commit(types.SET_SURVEY, response.data)
          endLoading(dispatch, loaderName)
          resolve(response.data)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Get buildingNote by survey
  * @param {Number} surveyID - Survey ID
  */
  async getBuildingNotes ({ commit, dispatch }, surveyId) {
    let loaderName = 'getBuildingNotes'
    startLoading(dispatch, loaderName)
    return new Promise((resolve, reject) => {
      api
        .getBuildingNotes(surveyId)
        .then((response) => {
          commit(types.SET_BUILDING_NOTE, response.data)
          endLoading(dispatch, loaderName)
          resolve(response.data)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Get survey by Key
  * @param {Number} surveyID - Survey ID
  */
  async getSurveyByKey ({ commit, dispatch }, { key, userId }) {
    let loaderName = 'getSurveyByKey'
    startLoading(dispatch, loaderName)
    return new Promise((resolve, reject) => {
      api
        .getSurveyByKey({ key, userId })
        .then((response) => {
          commit(types.SET_SURVEY, response.data)
          endLoading(dispatch, loaderName)
          resolve(response.data)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Get all surveys
  * @param {Object} survey - Object with buildingID and assignedTo
  */
  async assignSurvey ({ commit, dispatch }, survey) {
    let loaderName = 'assignSurvey'
    startLoading(dispatch, loaderName)

    return new Promise((resolve, reject) => {
      api
        .assignSurvey(survey)
        .then((response) => {
          commit(types.UPDATE_INCOMPLETE_SURVEYS_COUNT, 1)
          endLoading(dispatch, loaderName)
          resolve(response.data)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Update a survey answer
  */
  async updateSurvey ({ commit, dispatch, state }, isSurveyComplete) {
    let loaderName = 'updateSurvey'
    startLoading(dispatch, loaderName)

    return new Promise((resolve, reject) => {
      // Create an object with the required format: Example: [{ questionID: 1, answerID: 4, photos: [] }, { questionID: 2, answerID: 3, photos: [] }]
      let answers = state.survey
      answers = answers.map((x) => {
        return {
          questionId: x.question.id,
          answerId: x.answer.id,
          photos: x.answer.photos || [],
          comments: x.answer.comments || []
        }
      })
      // Update on database
      api
        .updateSurvey({
          id: state.surveyDetails.id,
          answers: JSON.stringify(answers),
          isComplete: isSurveyComplete
        })
        .then((response) => {
          endLoading(dispatch, loaderName)
          if (isSurveyComplete) {
            commit(types.UPDATE_INCOMPLETE_SURVEYS_COUNT, -1)
          }
          resolve(response)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Update a survey answer
  * @param {FormData} formData - FormData with photo, auditId, questionId
  * @param {Number} surveyId - Survey ID
  * @param {Number} questionId - Question ID
  */
  async uploadSurveyPhoto ({ commit, dispatch, state }, { formData, surveyId, questionId }) {
    let loaderName = 'uploadSurveyPhoto'
    startLoading(dispatch, loaderName)

    return new Promise((resolve, reject) => {
      api
        .uploadSurveyPhoto(formData)
        .then((response) => {
          if (response.data) {
            commit(types.ADD_PHOTO, { questionId, filename: response.data })
            resolve(response.data)
          } else {
            reject(new Error('Something went wrong!'))
          }
          endLoading(dispatch, loaderName)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Delete a survey answer
  * @param {Number} questionId - QuestionID
  * @param {String} filename - Filename
  */
  async deleteSurveyPhoto ({ commit, dispatch, state }, { surveyId, questionId, filename }) {
    let loaderName = 'deleteSurveyPhoto'
    startLoading(dispatch, loaderName)

    return new Promise((resolve, reject) => {
      api
        .deleteSurveyPhoto({ surveyId, questionId, filename })
        .then((response) => {
          if (response) {
            commit(types.REMOVE_PHOTO, { questionId, filename })
            resolve(response)
          } else {
            reject(new Error('Something went wrong'))
          }
          endLoading(dispatch, loaderName)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Update a business note
  * @param {Number} auditId - AuditId
  * @param {String} comment - Comment
  * @param {Number} isDefault - default
  */
  async updateBuildingNote ({ commit, dispatch, state }, { surveyId, comment, isDefault }) {
    let loaderName = 'updateBuildingNote'
    startLoading(dispatch, loaderName)
    console.log(comment)
    state.surveyDetails.buildingNotes = comment
    // console.log(state)
    // console.log(state.surveyDetails)
    // console.log(state.surveyDetails.buildingNotes)

    return new Promise((resolve, reject) => {
      api
        .updateBuildingNote({ surveyId, comment, isDefault })
        .then((response) => {
          if (response) {
            // commit(types.UPDATE_BUILDING_NOTE, { surveyId, comment, isDefault })
            resolve(response)
          } else {
            reject(new Error('Something went wrong'))
          }
          endLoading(dispatch, loaderName)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Notify admins when a survey is complete
  * @param {Number} questionId - QuestionID
  * @param {String} filename - Filename
  */
  async notifyAdminsSurveyIsComplete ({ commit, dispatch, state }) {
    let loaderName = 'notifyAdminsSurveyIsComplete'
    startLoading(dispatch, loaderName)
    return new Promise((resolve, reject) => {
      api
        .notifyAdminsSurveyIsComplete(state.surveyDetails.id)
        .then((response) => {
          if (response) {
            resolve(response)
          } else {
            reject(new Error('Something went wrong'))
          }
          endLoading(dispatch, loaderName)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Get incomplete surveys count
  */
  async getIncompleteSurveysCount ({ commit, dispatch, state }) {
    let loaderName = 'getIncompleteSurveysCount'
    startLoading(dispatch, loaderName)
    return new Promise((resolve, reject) => {
      api
        .getIncompleteSurveysCount()
        .then((response) => {
          commit(types.SET_INCOMPLETE_SURVEYS_COUNT, response.data)
          endLoading(dispatch, loaderName)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Get Labor Analysis by location
  */
  async getLaborAnalysis ({ commit, dispatch, state }, locationId) {
    let loaderName = 'getLaborAnalysis'
    startLoading(dispatch, loaderName)
    return new Promise((resolve, reject) => {
      api
        .getLaborAnalysis(locationId)
        .then((response) => {
          resolve(response.data)
          endLoading(dispatch, loaderName)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Send report by email
  * @param {String} email - recipient email address
  */
  async sendReportByEmail ({ commit, dispatch, state }, email) {
    let loaderName = 'sendReportByEmail'
    startLoading(dispatch, loaderName)
    return new Promise((resolve, reject) => {
      api
        .sendReportByEmail({ surveyKey: state.surveyDetails.key.toLowerCase(), email })
        .then((response) => {
          resolve(response.data)
          endLoading(dispatch, loaderName)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
  * Download Report
  * @param {Object} survey - Survey
  */
  downloadReport
}



// WEBPACK FOOTER //
// ./src/store/modules/survey/actions.js